import { Group, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import type { DeliveryPlanRow } from "../../api/types";

export type Breakdown = Pick<
  DeliveryPlanRow,
  | "delivered"
  | "delivered_debt"
  | "delivered_exchange"
  | "gross"
  | "gross_normal"
  | "gross_exchange"
  | "gross_debt"
  | "returned"
  | "returned_unprocessed"
  | "moved_out"
  | "foreign_returned"
>;

const FIELDS: (keyof Breakdown)[] = [
  "delivered",
  "delivered_debt",
  "delivered_exchange",
  "gross",
  "gross_normal",
  "gross_exchange",
  "gross_debt",
  "returned",
  "returned_unprocessed",
  "moved_out",
  "foreign_returned",
];

export function sumBreakdown(rows: Breakdown[]): Breakdown {
  const out = Object.fromEntries(FIELDS.map((f) => [f, 0])) as Breakdown;
  for (const r of rows) for (const f of FIELDS) out[f] += r[f] ?? 0;
  return out;
}

function Line({ label, value, c, fw, pl }: { label: string; value: number; c?: string; fw?: number; pl?: number }) {
  return (
    <Group justify="space-between" gap="xs" wrap="nowrap" pl={pl}>
      <Text size="xs" c={c ?? "dimmed"} fw={fw}>{label}</Text>
      <Text size="xs" ff="monospace" c={c} fw={fw}>{value}</Text>
    </Group>
  );
}

/** How the net "delivered" figure is built: gross shipped → minus returned → net,
 * plus what the net figure leaves out (moved to other customers, foreign returns). */
export function PlanBreakdown({ b }: { b: Breakdown }) {
  const { t } = useTranslation();
  const k = (s: string) => t(`shipmentStats.plan.bd.${s}`);
  return (
    <Stack gap={1}>
      <Line label={k("billed")} value={b.gross_normal} fw={700} c="teal" />
      <Line label={k("gross")} value={b.gross} fw={600} c="var(--mantine-color-text)" />
      <Line label={k("normal")} value={b.gross_normal} pl={10} />
      <Line label={k("exchange")} value={b.gross_exchange} pl={10} />
      <Line label={k("debt")} value={b.gross_debt} pl={10} c={b.gross_debt > 0 ? "orange" : undefined} />
      <Line label={k("returned")} value={-b.returned} c={b.returned > 0 ? "red" : undefined} />
      {b.returned_unprocessed > 0 && (
        <Line label={k("unprocessed")} value={b.returned_unprocessed} pl={10} c="yellow.7" />
      )}
      {b.gross_debt > 0 && <Line label={k("debtExcluded")} value={-(b.delivered_debt)} />}
      <Line label={k("net")} value={b.delivered} fw={700} c="var(--mantine-color-text)" />
      <Line label={k("netExchange")} value={b.delivered_exchange} pl={10} />
      {b.moved_out > 0 && <Line label={k("movedOut")} value={b.moved_out} c="grape" />}
      {b.foreign_returned > 0 && <Line label={k("foreign")} value={b.foreign_returned} c="grape" />}
    </Stack>
  );
}
